import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { getAuth } from "firebase/auth";
import config from "infra/config";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import app from "infra/packages/firebase";
import { logError } from "infra/packages/@sentry/logError";

const auth = getAuth(app);

const http = axios.create({
  baseURL: config.urlApi,
});

export const httpUploads = axios.create({
  baseURL: config.urlUploads,
});

const requestInterceptor = async (
  config: AxiosRequestConfig
): Promise<AxiosRequestConfig> => {
  const { tenant } = useCurrentTenant.getState();

  if (tenant.id && !config.headers?.tenant) {
    config.headers["tenant"] = tenant.id;
  }

  config.headers["locale"] = "pt-BR";

  const token = await getToken();

  if (token && !config.headers["authorization"]) {
    config.headers["authorization"] = `Bearer ${token}`;
  }

  return config;
};

const requestErrorHandler = (error: AxiosError) => {
  Promise.reject(error);
};

const responseErrorHandler = async (error: any) => {
  const { config } = error;
  console.log("config method", config.method);
  if (config.dontRetry && !error.response) {
    console.log("Return without throwing", config.url);
    return logError(error);
  }

  if (
    !config ||
    config.dontRetry ||
    (error.response && error.response?.status !== 401) ||
    config.method?.toLowerCase?.() !== "get"
  ) {
    throw error;
  }

  const delay = error.response?.status === 401 ? 5000 : 10000;
  config.dontRetry = true;
  console.log(`Retry after ${delay}s...`);

  const delayRetryRequest = new Promise((resolve) => {
    if (error.response?.status === 401) {
      auth.currentUser?.getIdToken(true);
    }

    setTimeout(() => {
      console.log("Retrying request", config.url);
      resolve(null);
    }, delay);
  });

  return delayRetryRequest.then(() => http(config));
};

http.interceptors.request.use(requestInterceptor, requestErrorHandler);

http.interceptors.response.use(undefined, responseErrorHandler);

httpUploads.interceptors.request.use(requestInterceptor, requestErrorHandler);

httpUploads.interceptors.response.use(undefined, responseErrorHandler);

export async function getToken() {
  const currentUser = auth.currentUser;

  let token = null;

  if (currentUser) {
    try {
      token = await currentUser.getIdToken();
    } catch (err) {
      token = await currentUser.getIdToken(true); // TODO, rever isso pois esta fazendo refresh token todo momento
    }
  }

  return token;
}

export default http;
